import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Facebook, Instagram, Linkedin, Clock, CheckCircle, FileText } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.status === 429) {
        toast.error('Too many requests. Please wait a minute before trying again.');
        setIsSubmitting(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit form');
      }

      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      
      // Reset form after delay
      setTimeout(() => {
        setFormData({ name: '', email: '', phone: '', message: '' });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.message || 'Failed to send message. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <main className="pt-20 sm:pt-24">
      {/* Hero Section */}
      <section className="relative py-16 sm:py-24 bg-slate-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80"
            alt="Construction background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-white/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-4 mb-6 animate-fade-in">
              <div className="h-1 w-16 bg-yellow-500" />
              <span className="text-yellow-600 font-bold tracking-wider text-sm uppercase">Contact Us</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 leading-tight mb-6 animate-fade-in-up animation-delay-200 uppercase tracking-tight">
              Let's Build Something Great Together
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed animate-fade-in-up animation-delay-400">
              Ready to start your project? Get in touch with our team for a free consultation 
              and quote. We're here to help bring your vision to life.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6 uppercase">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    autoComplete="name"
                    data-testid="contact-name-input"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors duration-300"
                    placeholder="John Smith"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                      inputMode="email"
                      data-testid="contact-email-input"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors duration-300"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                      inputMode="tel"
                      data-testid="contact-phone-input"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors duration-300"
                      placeholder="+27 21 000 0000"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">
                    Your Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    data-testid="contact-message-input"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-colors duration-300 resize-none"
                    placeholder="Tell us about your project..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || isSubmitted}
                  data-testid="contact-submit-btn"
                  className={`w-full sm:w-auto px-8 py-4 font-bold uppercase tracking-wide rounded-sm transition-all duration-300 flex items-center justify-center gap-2 ${
                    isSubmitted
                      ? 'bg-green-500 text-white'
                      : 'bg-yellow-500 text-slate-900 hover:bg-yellow-400 shadow-md hover:shadow-lg'
                  } disabled:opacity-70 disabled:cursor-not-allowed`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : isSubmitted ? (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Message Sent!
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6 uppercase">Get in Touch</h2>
              
              <div className="space-y-6 mb-12">
                {/* Phone */}
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="flex items-start gap-4 p-6 bg-slate-50 border border-slate-100 rounded-sm group hover:border-yellow-500/50 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-sm bg-yellow-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-yellow-500 transition-colors duration-300">
                    <Phone className="w-6 h-6 text-yellow-500 group-hover:text-slate-900 transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-bold mb-1">Call Us</h3>
                    <p className="text-slate-500 text-sm mb-2">Mon-Fri, 7am-5pm</p>
                    <p className="text-yellow-600 font-bold">{companyInfo.phone}</p>
                  </div>
                </a>

                {/* Email */}
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-start gap-4 p-6 bg-slate-50 border border-slate-100 rounded-sm group hover:border-yellow-500/50 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-sm bg-yellow-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-yellow-500 transition-colors duration-300">
                    <Mail className="w-6 h-6 text-yellow-500 group-hover:text-slate-900 transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-bold mb-1">Email Us</h3>
                    <p className="text-slate-500 text-sm mb-2">We'll respond within 24 hours</p>
                    <p className="text-yellow-600 font-bold">{companyInfo.email}</p>
                  </div>
                </a>

                {/* Address */}
                <div className="flex items-start gap-4 p-6 bg-slate-50 border border-slate-100 rounded-sm">
                  <div className="w-12 h-12 rounded-sm bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-bold mb-1">Visit Us</h3>
                    <p className="text-slate-500 text-sm mb-2">By appointment only</p>
                    <p className="text-yellow-600 font-bold">{companyInfo.address}</p>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4 p-6 bg-slate-50 border border-slate-100 rounded-sm">
                  <div className="w-12 h-12 rounded-sm bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div>
                    <h3 className="text-slate-900 font-bold mb-1">Business Hours</h3>
                    <p className="text-slate-600 text-sm">Monday - Friday: 7:00 AM - 5:00 PM</p>
                    <p className="text-slate-600 text-sm">Saturday: By appointment</p>
                    <p className="text-slate-600 text-sm">Sunday: Closed</p>
                  </div>
                </div>
              </div>

              {/* Company Registration Info */}
              <div className="p-6 bg-slate-900 rounded-sm mb-8">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2 uppercase">
                  <FileText className="w-5 h-5 text-yellow-500" />
                  Company Details
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Company:</span>
                    <p className="text-slate-300">{companyInfo.fullName}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Reg No:</span>
                    <p className="text-slate-300">{companyInfo.registrationNumber}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">VAT Status:</span>
                    <p className="text-slate-300">VAT Compliant</p>
                  </div>
                  <div>
                    <span className="text-slate-500">BIBC No:</span>
                    <p className="text-slate-300">{companyInfo.bibcNumber}</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="text-slate-900 font-bold mb-4 uppercase">Follow Us</h3>
                <div className="flex gap-4">
                  <a
                    href={companyInfo.socials.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-sm bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-yellow-500 hover:border-yellow-500 hover:text-slate-900 transition-all duration-300"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a
                    href={companyInfo.socials.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-sm bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-yellow-500 hover:border-yellow-500 hover:text-slate-900 transition-all duration-300"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href={companyInfo.socials.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-sm bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-yellow-500 hover:border-yellow-500 hover:text-slate-900 transition-all duration-300"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps */}
      <section className="h-72 sm:h-96 bg-slate-200 relative">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3311.0673!2d18.5677!3d-33.9167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1dcc5c7a4e6b7b3b%3A0x0!2s8%20Matthee%20Street%2C%20Parow%20East%2C%20Cape%20Town%2C%207500!5e0!3m2!1sen!2sza!4v1699999999999!5m2!1sen!2sza"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Barchalo Properties Location"
          className="grayscale-[50%] hover:grayscale-0 transition-all duration-500"
        />
        {/* Overlay with address info */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900 to-transparent p-6">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-yellow-500 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-slate-900" />
              </div>
              <div>
                <p className="text-white font-bold">8 Matthee Street, Parow East</p>
                <p className="text-slate-400 text-sm">Western Cape, 7500, South Africa</p>
              </div>
            </div>
            <a
              href="https://www.google.com/maps/search/8+Matthee+Street,+Parow+East,+Cape+Town,+7500"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2.5 bg-yellow-500 text-slate-900 font-bold uppercase tracking-wide rounded-sm hover:bg-yellow-400 transition-colors duration-300"
            >
              Get Directions
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
